import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'antd';
import { useParams } from 'react-router-dom';

import {
  CustomNotification,
  dialogFunction,
  FormCancelButton,
  FormDeleteButton,
  StyledModal,
  WithLoading,
} from 'common/components';
import { CERTIFICATE_ACTIONS } from './constants';
import SelectedProducts from './SelectedProducts';

import * as actionsGridView from 'common/components/grid-view/controllers/actions';
import gridSelector from 'common/components/grid-view/controllers/selectors';
import {
  useGetCertificatesByProductIds,
  useUpdateCertificate,
  useValidateCertificate,
} from './hook';
import { useGetProductFullView } from 'hooks';
import EmptyCertificate from './EmptyCertificate';

const DeleteCertificateModal = (props) => {
  const { certificateActions, setCertificateActions, isFullView } = props;

  const { id: _id, productId: _productId } = useParams();
  const id = _id || _productId;

  let products = useSelector(
    gridSelector.makeSelectDetailCurrentITemsSelection()
  );

  const dispatch = useDispatch();
  const { productFull } = useGetProductFullView({ productId: id });
  const [certificateList, setCertificateList] = useState();

  const { unlinkMultipleCertificates } = useUpdateCertificate();

  const handleRemoveProduct = (productItem) => {
    setCertificateList((prev) => {
      return prev?.filter(
        (product) =>
          product?.certificate?.linkId !== productItem?.certificate?.linkId
      );
    });
  };

  const onCancel = () => {
    setCertificateActions(null);
  };

  const handleSubmit = async () => {
    try {
      const linkIds =
        certificateList?.map((product) => product?.certificate?.linkId) || [];
      const params = {
        linkIds,
      };

      unlinkMultipleCertificates.mutate(params, {
        onSuccess: ({ isSuccess, message, data }) => {
          if (isSuccess) {
            CustomNotification.success('Certificate deleted successfully.');
            setCertificateActions(null);
            if (!isFullView) {
              dispatch(actionsGridView.updateItemsSelection([]));
              dispatch(actionsGridView.updateDetailCurrentItemsSelection([]));
            }
          } else {
            if (data?.lockedProducts?.length > 0) {
              showLockedProducts(data?.lockedProducts);
            } else
              CustomNotification.error(
                message || 'Failed to delete certificate.'
              );
          }
        },
      });
    } catch (error) {
      console.error(error);
      CustomNotification.error('Server Error!');
    }
  };

  const handleSetCertificate = (productCertificates) => {
    const certificateList = [];
    productCertificates?.forEach((product) => {
      product?.certificates?.forEach((certificate) => {
        certificateList?.push({
          ...product,
          certificate: { ...certificate },
        });
      });
    });

    setCertificateList(certificateList);
  };

  const { showLockedProducts } = useValidateCertificate({
    onSubmit: () => {},
  });

  const visible = certificateActions === CERTIFICATE_ACTIONS.DELETE;
  const productIds = products?.map((item) => item?.id);
  const params = {
    productIds: [...(isFullView ? [productFull?.productId] : productIds)],
  };

  const { isLoading } = useGetCertificatesByProductIds({
    params,
    onSuccess: handleSetCertificate,
    enabled: visible,
    staleTime: 0,
  });

  const hasCertificate = certificateList?.length > 0;

  return (
    <StyledModal
      title='Delete Certificate'
      visible={certificateActions === CERTIFICATE_ACTIONS.DELETE}
      onCancel={onCancel}
      maskClosable={false}
      width={1280}
      bodyStyle={{ height: 'min(600px, 60vh)' }}
      destroyOnClose
      footer={[
        <FormCancelButton onClick={onCancel} />,
        <FormDeleteButton
          text='Delete All'
          onClick={() => {
            dialogFunction({
              type: 'warn',
              content: `Are you sure you want to delete all certificates?`,
              okText: 'Submit',
              okButtonProps: {
                type: 'primary',
              },
              onOk: async () => await handleSubmit(),
            });
          }}
          disabled={!hasCertificate}
          loading={unlinkMultipleCertificates?.isLoading}
        />,
      ]}
    >
      <WithLoading loading={isLoading}>
        <Row style={{ height: '100%' }}>
          <Col span={24}>
            {hasCertificate ? (
              <SelectedProducts
                productList={certificateList}
                handleRemoveProduct={handleRemoveProduct}
                showCheckbox={false}
              />
            ) : (
              <EmptyCertificate />
            )}
          </Col>
        </Row>
      </WithLoading>
    </StyledModal>
  );
};

export default DeleteCertificateModal;
